import { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Check, X, ChevronDown, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Package, Subscription } from '../api/billing';
import { cn } from './ui/utils';

interface PlanComparisonDialogProps {
  packages: Package[];
  currentPackage: Subscription | null;
  onChoosePlan: (packageId: number) => void;
  onContactSales: () => void;
  isUpdating: boolean;
}

/**
 * How much of a pricing-page feature row is actually backed by
 * AccessControlService/EntitlementService today (see
 * api/libs/constants/package-entitlements.ts):
 *  - 'enforced': a real endpoint calls @RequirePermission for this module, so
 *    the entitlement check actually blocks the request server-side, and the
 *    matching frontend control disables itself with an upgrade tooltip
 *    (custom-role management, license policy customization, and the
 *    comprehensive Excel scan report all do this today).
 *  - 'tracked': the entitlement grid has real data for this module/action,
 *    but no endpoint calls @RequirePermission on it yet -- the plan/tier cut
 *    is decided, just not wired to a live 403 yet.
 *  - 'planned': no MODULE_CATALOG module exists for this feature at all --
 *    purely a roadmap/marketing row today.
 */
type GatingStatus = 'enforced' | 'tracked' | 'planned';

interface FeatureRow {
  label: string;
  /** One value per tier: Community, Professional, Business, Enterprise. `true`/`false` render as check/x; a string renders as-is. */
  values: [boolean | string, boolean | string, boolean | string, boolean | string];
  gating: GatingStatus;
  /** What actually gates this row today, shown in the tooltip. */
  note?: string;
  /**
   * Feature isn't built yet -- renders a "Coming Soon" badge and mutes the
   * tier ticks so the row doesn't read as already-shipped. Orthogonal to
   * `gating`, which is about whether entitlement checks are wired up: most
   * 'planned' rows (Dashboard, Component Hub, ORT download) ship today and are
   * simply ungated, so they must not pick this up by default.
   */
  comingSoon?: boolean;
}

const FEATURE_ROWS: FeatureRow[] = [
  { label: 'Dashboard & Analytics', values: ['Basic', 'Full', 'Full', 'Full'], gating: 'planned' },
  { label: 'ORT File Download', values: [true, true, true, true], gating: 'planned' },
  { label: 'GitHub Actions Automation', values: [true, true, true, true], gating: 'planned' },
  { label: 'Auditing / Curation', values: [true, true, true, true], gating: 'planned', comingSoon: true },
  { label: 'Third-Party License Obligations', values: [true, true, true, true], gating: 'planned' },
  { label: 'Component Hub', values: [true, true, true, true], gating: 'planned' },
  {
    label: 'Component Hub Export', values: [false, true, true, true], gating: 'tracked',
    note: 'Backed by the components module’s "export" action -- the export button is a client-side CSV dump with no backend endpoint, so it\'s gated by disabling the button off-plan rather than by a server-side 403.',
  },
  {
    label: 'Comprehensive Excel Report', values: [false, true, true, true], gating: 'enforced',
    note: 'Backed by the scans module’s "export" action (GET /scans/:id/download-scc-report, the "Export Scan Report" button) -- actively enforced: a Community-tier workgroup gets a 403 server-side, and the button is disabled client-side.',
  },
  {
    label: 'GitHub App Integration', values: [false, true, true, true], gating: 'tracked',
    note: 'Backed by the integrations module -- data-driven today, not yet enforced by an endpoint.',
  },
  {
    label: 'License Policy Customization', values: [false, true, true, true], gating: 'enforced',
    note: 'Backed by the policies module’s create/delete actions -- actively enforced: a Community-tier workgroup gets a 403 server-side from saving or deleting custom policies, and the relevant buttons are disabled client-side.',
  },
  {
    label: 'Conditional Alerts & Notifications', values: [false, true, true, true], gating: 'tracked',
    note: 'Backed by the notifications module’s "manage" action -- data-driven today, not yet enforced by an endpoint.',
  },
  {
    label: 'RBAC', values: [false, true, true, true], gating: 'enforced',
    note: 'Backed by the roles module’s create/update/delete actions -- actively enforced: a Community-tier workgroup is blocked server-side from managing custom roles.',
  },
  { label: 'Dependency Analysis View', values: [false, false, true, true], gating: 'planned' },
  { label: 'Provenance Data', values: [false, false, true, true], gating: 'planned' },
  { label: 'Cryptography Data / CBOM', values: [false, false, true, true], gating: 'planned' },
  { label: 'SBOM Upload / Import', values: [false, false, true, true], gating: 'planned' },
  {
    label: 'Evaluator Rules Setting', values: [false, false, true, true], gating: 'tracked',
    note: 'Backed by the policies module’s "configure_rules" action -- Business+ only (stricter than License Policy Customization), data-driven today, not yet enforced by an endpoint.',
  },
  { label: 'SSO / SAML Integration', values: [false, false, false, true], gating: 'planned' },
  { label: 'Jira / Slack / Webhook Integrations', values: [false, false, false, true], gating: 'planned' },
  { label: 'On-Premises / Private Cloud Deployment', values: [false, false, false, true], gating: 'planned' },
  { label: 'Dedicated Customer Success Manager', values: [false, false, false, true], gating: 'planned' },
  { label: 'Custom Reporting Templates', values: [false, false, false, true], gating: 'planned' },
  { label: 'CI/CD Integration', values: [false, false, false, true], gating: 'planned' },
  { label: 'API Availability', values: [false, false, false, true], gating: 'planned' },
  {
    label: 'Support',
    values: ['Community / Docs', 'Email, 2 Business Day Response', 'Email, Next Business Day', 'Dedicated Slack, Priority Response'],
    gating: 'planned',
  },
];

const LIMIT_ROWS: { label: string; key: keyof NonNullable<Package['limits']>; suffix?: string; describe?: (v: any) => string }[] = [
  { label: 'Scans / month', key: 'scanLimit' },
  { label: 'Users included', key: 'teamMemberLimit' },
  { label: 'Extra user price', key: 'extraUserPrice', describe: (v) => `$${v} / user / month` },
  { label: 'ORT file uploads / month', key: 'ortUploadLimit' },
  { label: 'Scan rollover', key: 'scanRollover', describe: (v) => (v ? 'Unused rolls to next month, resets at year end' : '') },
  { label: 'Trial of paid tier', key: 'trial', describe: (v) => v },
];

function formatLimitCell(pkg: Package, row: (typeof LIMIT_ROWS)[number]): string {
  const raw = pkg.limits?.[row.key];
  if (raw === undefined || raw === null || raw === false || raw === -1) return '—';
  return row.describe ? row.describe(raw) : String(raw);
}

function tierTag(code: string): string {
  return `orbit8/${code.toLowerCase()}`;
}

function priceDisplay(pkg: Package): { price: string; period: string } {
  if (pkg.packagetype === 'CUSTOM') return { price: 'Custom', period: 'Contact sales' };
  if (!pkg.ispaid || pkg.price === 0) return { price: 'Free', period: 'Forever, limited functionality' };
  return { price: `$${pkg.price}`, period: '/ month, USD' };
}

function GatingBadge({ status, note }: { status: GatingStatus; note?: string }) {
  if (status === 'planned') return null;
  const Icon = status === 'enforced' ? ShieldCheck : ShieldAlert;
  const colorClass = status === 'enforced' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Icon className={cn('w-3.5 h-3.5 ml-1.5 inline-block align-text-bottom cursor-help', colorClass)} />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{note}</TooltipContent>
    </Tooltip>
  );
}

/**
 * `muted` greys out an otherwise-positive cell: the tier does include the
 * feature, but it isn't built yet (see FeatureRow.comingSoon), so a full-colour
 * green tick would overstate it. Absent cells already render muted, so they
 * need no special casing.
 */
function FeatureCell({ value, muted }: { value: boolean | string; muted?: boolean }) {
  if (typeof value === 'string') return <span className={cn('text-sm', muted && 'text-muted-foreground/60')}>{value}</span>;
  return value ? (
    <Check className={cn('w-4 h-4 mx-auto', muted ? 'text-muted-foreground/50' : 'text-green-600 dark:text-green-400')} />
  ) : (
    <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
  );
}

export function PlanComparisonDialog({ packages, currentPackage, onChoosePlan, onContactSales, isUpdating }: PlanComparisonDialogProps) {
  const [limitsOpen, setLimitsOpen] = useState(true);
  const [featuresOpen, setFeaturesOpen] = useState(true);

  const sortedPackages = [...packages].sort((a, b) => (a.displayorder ?? 0) - (b.displayorder ?? 0));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Change your plan</h2>
        <p className="text-sm text-muted-foreground mt-1">Compare tiers and pick the plan that fits your scan volume.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {sortedPackages.map((pkg) => {
          const isCurrent = currentPackage?.package.id === pkg.id;
          const { price, period } = priceDisplay(pkg);
          // Payment methods are temporarily disabled, so any paid, non-custom
          // tier can't actually be checked out -- only the free tier and the
          // contact-sales (Enterprise) tier stay selectable for now.
          const isPaymentGated = pkg.ispaid && pkg.packagetype !== 'CUSTOM';

          return (
            <div
              key={pkg.id}
              className={cn(
                'rounded-lg border flex flex-col overflow-hidden',
                isCurrent ? 'border-primary ring-1 ring-primary bg-accent/30' : 'border-border',
              )}
            >
              <div className="flex flex-col gap-3 p-4 flex-1">
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground font-mono">{tierTag(pkg.code)}</span>
                  <h3 className="font-semibold">{pkg.title}</h3>
                  {pkg.code === 'COMMUNITY' && (
                    <Badge variant="outline" className="text-xs">Forever free</Badge>
                  )}
                </div>

                <div>
                  <div className="text-2xl font-bold text-foreground">{price}</div>
                  <div className="text-xs text-muted-foreground">{period}</div>
                </div>

                {isCurrent ? (
                  <Button
                    variant="outline"
                    className="mt-auto gap-1.5 border-green-300 bg-green-100 text-green-800 opacity-100 dark:border-green-800 dark:bg-green-900 dark:text-green-200"
                  >
                    <Check className="w-3 h-3" /> Current Plan
                  </Button>
                ) : pkg.packagetype === 'CUSTOM' ? (
                  <Button
                    variant="link"
                    className="mt-auto justify-start px-0 text-primary"
                    onClick={onContactSales}
                  >
                    Contact sales &rarr;
                  </Button>
                ) : isPaymentGated ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="mt-auto inline-block">
                        <Button
                          variant="link"
                          className="justify-start px-0 text-muted-foreground"
                          disabled
                        >
                          Choose this plan &rarr;
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Paid plans are temporarily unavailable.</TooltipContent>
                  </Tooltip>
                ) : (
                  <Button
                    variant="link"
                    className="mt-auto justify-start px-0 text-primary"
                    disabled={isUpdating}
                    onClick={() => onChoosePlan(pkg.id)}
                  >
                    {isUpdating ? 'Updating...' : 'Choose this plan →'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Collapsible open={limitsOpen} onOpenChange={setLimitsOpen} className="border rounded-lg">
        <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium">
          Limits
          <ChevronDown className={cn('w-4 h-4 transition-transform', limitsOpen && 'rotate-180')} />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 pb-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left font-medium text-muted-foreground py-2 pr-4"> </th>
                  {sortedPackages.map((pkg) => (
                    <th key={pkg.id} className="text-left font-medium text-muted-foreground py-2 px-4">{pkg.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {LIMIT_ROWS.map((row) => (
                  <tr key={row.label} className="border-b last:border-0">
                    <td className="py-2 pr-4 text-muted-foreground">{row.label}</td>
                    {sortedPackages.map((pkg) => (
                      <td key={pkg.id} className="py-2 px-4">{formatLimitCell(pkg, row)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={featuresOpen} onOpenChange={setFeaturesOpen} className="border rounded-lg">
        <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium">
          Features ({FEATURE_ROWS.length})
          <ChevronDown className={cn('w-4 h-4 transition-transform', featuresOpen && 'rotate-180')} />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 pb-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left font-medium text-muted-foreground py-2 pr-4"> </th>
                  {sortedPackages.map((pkg) => (
                    <th key={pkg.id} className="text-left font-medium text-muted-foreground py-2 px-4">{pkg.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_ROWS.map((row) => (
                  <tr key={row.label} className="border-b last:border-0">
                    <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">
                      {row.label}
                      {row.comingSoon && (
                        <Badge className="ml-2 px-1.5 py-0 text-[10px] align-middle">Coming Soon</Badge>
                      )}
                      {/*<GatingBadge status={row.gating} note={row.note} />*/}
                    </td>
                    {row.values.map((value, i) => (
                      <td key={i} className="py-2 px-4">
                        <FeatureCell value={value} muted={row.comingSoon} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/*<div className="mt-4 pt-3 border-t text-xs text-muted-foreground space-y-1.5">
            <p className="font-medium text-foreground">What the icons next to a feature mean:</p>
            <p className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
              Enforced today -- the backend actually blocks this action for tiers that don't include it.
            </p>
            <p className="flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              Tracked, enforcement rolling out -- the plan cut is decided in our entitlement data, but no endpoint checks it yet.
            </p>
            <p>No icon -- roadmap feature; there's no gating mechanism for it yet.</p>
          </div>*/}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export default PlanComparisonDialog;
